/**
 * Collapsible desktop sidebar: what state it is in and why.
 *
 * Two inputs decide it. The user's own preference (collapsed or not) is kept
 * per browser. Pages that need every pixel (Verläufe: list | chat | context)
 * collapse the sidebar on entry; a manual toggle there only lasts for that
 * visit, so leaving the page restores the user's own preference untouched.
 *
 * Pure functions only — the React provider (SidebarContext) wires them to the
 * router and to localStorage.
 */

/**
 * Pages that collapse the sidebar on entry. A route matches itself and
 * everything below it (`/conversations/42`); extend the list for further
 * full-width pages.
 */
export const AUTO_COLLAPSE_ROUTES: readonly string[] = Object.freeze(['/conversations']);

export const SIDEBAR_COLLAPSED_KEY = 'tg.sidebar.collapsed.v1';

/**
 * True for the route itself and every path below it, not for
 * `/conversations-x`. Case-insensitive like React Router's matching.
 */
export function matchesRoute(pathname: string, route: string): boolean {
  const path = pathname.toLowerCase();
  const base = route.toLowerCase();
  return path === base || path.startsWith(`${base}/`);
}

/** The auto-collapse route the path belongs to, or null for an ordinary page. */
export function autoCollapseRouteFor(
  pathname: string,
  routes: readonly string[] = AUTO_COLLAPSE_ROUTES,
): string | null {
  return routes.find((route) => matchesRoute(pathname, route)) ?? null;
}

export interface SidebarVisit {
  /** The auto-collapse route being visited. */
  route: string;
  collapsed: boolean;
}

export interface SidebarState {
  /** The user's own choice, used on every ordinary page (persisted). */
  preference: boolean;
  /** Set while the user is on an auto-collapse page; overrides the preference. */
  visit: SidebarVisit | null;
}

export type SidebarAction =
  | { type: 'navigate'; pathname: string; routes?: readonly string[] }
  | { type: 'toggle' };

function visitFor(route: string | null): SidebarVisit | null {
  return route ? { route, collapsed: true } : null;
}

export function initialSidebarState(
  pathname: string,
  preference: boolean,
  routes: readonly string[] = AUTO_COLLAPSE_ROUTES,
): SidebarState {
  return { preference, visit: visitFor(autoCollapseRouteFor(pathname, routes)) };
}

export function sidebarReducer(state: SidebarState, action: SidebarAction): SidebarState {
  if (action.type === 'navigate') {
    const route = autoCollapseRouteFor(action.pathname, action.routes);
    // Same visit (e.g. a URL parameter inside Verläufe) or still on ordinary
    // pages: keep the object so React skips the re-render.
    if ((state.visit?.route ?? null) === route) return state;
    return { ...state, visit: visitFor(route) };
  }
  if (state.visit) {
    return { ...state, visit: { ...state.visit, collapsed: !state.visit.collapsed } };
  }
  return { ...state, preference: !state.preference };
}

export function isSidebarCollapsed(state: SidebarState): boolean {
  return state.visit ? state.visit.collapsed : state.preference;
}

/** Stored preference; expanded when nothing (valid) is stored or storage is blocked. */
export function loadSidebarPreference(): boolean {
  try {
    return window.localStorage.getItem(SIDEBAR_COLLAPSED_KEY) === '1';
  } catch {
    return false;
  }
}

export function saveSidebarPreference(collapsed: boolean): void {
  try {
    window.localStorage.setItem(SIDEBAR_COLLAPSED_KEY, collapsed ? '1' : '0');
  } catch {
    // Private window / quota: the toggle still applies for this page view.
  }
}

type ShortcutKeys = Pick<KeyboardEvent, 'key' | 'code' | 'metaKey' | 'ctrlKey' | 'altKey' | 'shiftKey' | 'repeat'>;

/** The letter B: by its character on Latin layouts, by the key position where the layout has no Latin letters. */
function isKeyB(event: Pick<KeyboardEvent, 'key' | 'code'>): boolean {
  if (/^[a-z]$/i.test(event.key)) return event.key.toLowerCase() === 'b';
  return event.code === 'KeyB';
}

/** ⌘B on the Mac, Ctrl+B elsewhere — no extra modifiers, no auto-repeat while held. */
export function isSidebarShortcut(event: ShortcutKeys): boolean {
  return (event.metaKey || event.ctrlKey) && !event.altKey && !event.shiftKey && !event.repeat && isKeyB(event);
}

/** Input types that take no typed text: the shortcut stays active on them. */
const NON_TEXT_INPUT_TYPES: ReadonlySet<string> = new Set([
  'button',
  'checkbox',
  'color',
  'file',
  'hidden',
  'image',
  'radio',
  'range',
  'reset',
  'submit',
]);

/** Where ⌘B / Ctrl+B belongs to the text (bold, cursor) and not to the sidebar. */
export function isEditableTarget(target: EventTarget | null): boolean {
  if (!(target instanceof HTMLElement)) return false;
  if (target instanceof HTMLInputElement) return !NON_TEXT_INPUT_TYPES.has(target.type);
  if (target instanceof HTMLTextAreaElement || target instanceof HTMLSelectElement) return true;
  // jsdom has no isContentEditable; the attribute lookup covers both.
  return target.isContentEditable === true || target.closest('[contenteditable]:not([contenteditable="false"])') !== null;
}

/**
 * Focus inside a dialog or popover: the shortcut would fold the sidebar
 * behind the overlay, where nobody sees it — leave the key alone there.
 */
export function isInsideDialog(target: EventTarget | null): boolean {
  return target instanceof Element && target.closest('[role="dialog"], [role="alertdialog"]') !== null;
}

/** Label of the shortcut for tooltips, in the notation of the user's platform. */
export function sidebarShortcutLabel(platform: string = typeof navigator === 'undefined' ? '' : navigator.platform): string {
  return /Mac|iPhone|iPad|iPod/i.test(platform) ? '⌘B' : 'Strg+B';
}
